import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/Sidebar';
import { ArrowLeft, UserPlus, Trash2, Shield, User } from 'lucide-react';
import './Events.css';
import './Dashboard.css';

function TeamManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [adding, setAdding] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  
  const navigate = useNavigate();
  const { theme } = useTheme();

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  useEffect(() => {
    loadAdmins();
    loadCurrentAdmin();
  }, []);

  const loadCurrentAdmin = async () => {
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/me', {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentAdmin(data);
      }
    } catch (error) {
      console.error('Error loading current admin:', error);
    }
  };

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/client/admins', {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error('Failed to load team members');
      }
      
      const data = await response.json();
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Error loading admins:', error);
      alert('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!email || !firstName || !lastName || !password) {
      alert('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    setAdding(true);
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/client/admins', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          password
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add team member');
      }
      
      alert('Team member added successfully!');
      setShowAddModal(false);
      setEmail('');
      setFirstName('');
      setLastName('');
      setPassword('');
      await loadAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      alert(error.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAdmin = async (adminId, adminName) => {
    if (!window.confirm(`Remove ${adminName} from your team?`)) {
      return;
    }
    
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`https://live-event-photos.onrender.com/api/admin/client/admins/${adminId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove team member');
      }
      
      alert('Team member removed successfully!');
      await loadAdmins();
    } catch (error) {
      console.error('Error removing admin:', error);
      alert(error.message);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/admin/login');
  };

  const adminData = JSON.parse(localStorage.getItem('adminData') || '{}');
  const isSuperAdmin = adminData?.role === 'super_admin';

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  const myRole = admins.find(a => a.id === currentAdmin?.id)?.role || 'member';
  // Clients (role='admin' in JWT) or owners in client_admins can add team members
  const isOwner = currentAdmin?.role === 'admin' || myRole === 'owner';

  return (
    <div className={`dashboard-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={toggleSidebar}
        onLogout={handleLogout}
        isSuperAdmin={isSuperAdmin}
      />
      
      <div className="events-content">
        <div className="events-top">
          <h2>Team Management</h2>
          <ThemeToggle />
        </div>
        <p style={{marginBottom: '24px', color: 'var(--text-secondary)'}}>Manage admin users for your account</p>
        
        <div>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>{admins.filter(a => a.role === 'member').length}</h3>
              <p>Team Members</p>
            </div>
            <div className="stat-card">
              <h3>{admins.filter(a => a.role === 'owner').length}</h3>
              <p>Owners</p>
            </div>
          </div>
          
          {isOwner && (
            <button className="btn-primary" onClick={() => setShowAddModal(true)} style={{marginBottom: '24px'}}>
              <UserPlus size={18} />
              Add Team Member
            </button>
          )}

          <div className="events-grid">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading team members...</p>
              </div>
            ) : admins.length === 0 ? (
              <div className="empty-state">
                <p>No team members found</p>
                <small>Add team members to share dashboard access</small>
              </div>
            ) : (
              admins.map(admin => (
                <div key={admin.id} className="event-card">
                  <div className="user-info">
                    <div className="user-header">
                      <h3>{admin.full_name}</h3>
                      <span className={`role-badge ${admin.role}`}>
                        {admin.role === 'owner' ? (
                          <><Shield size={14} style={{display: 'inline', marginRight: '4px'}} /> Owner</>
                        ) : (
                          <><User size={14} style={{display: 'inline', marginRight: '4px'}} /> Member</>
                        )}
                      </span>
                    </div>
                    <p className="user-detail">📧 {admin.email}</p>
                    <p className="user-detail" style={{fontSize: '12px', color: '#6B7280'}}>
                      Added {new Date(admin.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {isOwner && admin.role !== 'owner' && admin.id !== currentAdmin?.id && (
                    <div className="user-actions">
                      <button 
                        className="btn-delete" 
                        style={{padding: '8px 16px', fontSize: '14px'}} 
                        onClick={() => handleRemoveAdmin(admin.id, admin.full_name)}
                      >
                        <Trash2 size={16} />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Team Member Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Team Member</h2>
            <p style={{marginBottom: '20px', color: '#6B7280'}}>
              Create a new admin account for your team
            </p>
            
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
              />
            </div>
            
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
              />
            </div>
            
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@company.com"
              />
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
              />
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddModal(false)}
                disabled={adding}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddAdmin}
                disabled={adding}
              >
                {adding ? 'Adding...' : 'Add Team Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
